/**
 * QuickView drawer controller.
 * - 支持 handle 触发与链接拦截
 * - 通过策略抽取页面片段注入 drawer
 * - 负责请求控制、无障碍与产品增强绑定
 */

(function () {
  'use strict';

  // ✅ 每种预览类型的“策略”都放这里
  const STRATEGY_MAP = {
    // 爬取产品信息（默认策略）
    product: {
      type: 'product',
      viewParam: 'quickview',
      contentSelectors: ['.product-section', '.section-faq.text--neutral.palette--light.bg--neutral'],
      fallbackToMain: false,
      enableProductEnhancements: true,
    },
    // 爬取政策Page信息
    policy: {
      type: 'policy',
      viewParam: null,
      contentSelectors: ['.text__standard.text-left'],
      fallbackToMain: true,
      enableProductEnhancements: false,
    },
  };

  // 🔒 冻结策略模板（防止运行时被误修改）
  Object.keys(STRATEGY_MAP).forEach((k) => Object.freeze(STRATEGY_MAP[k]));
  Object.freeze(STRATEGY_MAP);

  const responseCache = new Map();

  const DEFAULT_OPTIONS = {
    // ✅ 1) 通过 data-product-handle 触发的 selector（string | string[]）
    trigger: '.product-carousel-quickview',

    // ✅ 2) 通过拦截 <a href> 触发的 selector（string | string[]）
    // 例如：'.shop-look-product-bottom a'
    linkTrigger: '.product-carousel-variants a.pc-variant:not(.pc-variant--more)',

    // Drawer/Modal selectors，不需要改动
    modal: '.product-quickview-custom',
    modalBody: '.product-quickview-custom .drawer__content-body',
    closeBtn: ['.product-quickview-custom .drawer__content-header button', '.product-quickview-custom .drawer__underlay'],

    // Link interception behavior，一般不需要改动
    interceptSameOriginOnly: true,
    ignoreLinkSchemes: ['#', 'mailto:', 'tel:'],

    // Debug logs，默认关闭
    debug: false,
  };

  const STATE = {
    initialized: false,
    triggerSelectors: new Set(),
    linkTriggerSelectors: new Set(),
    options: { ...DEFAULT_OPTIONS },
    isOpen: false,
    isLoading: false,
    requestToken: 0,
    activeAbortController: null,
    lastTrigger: null,
    lastOpenRequest: null,
    modalCleanup: null,
  };

  const QUICKVIEW_LOADER_HTML = `
    <div class="qv-loading" role="status" aria-live="polite" aria-label="Loading quick view">
      <svg viewBox="25 25 50 50">
        <circle r="20" cy="50" cx="50"></circle>
      </svg>
    </div>
  `;

  const QUICKVIEW_ERROR_HTML = `
    <div class="qv-status qv-status--error" role="alert">
      <p>Content loading failed, please try again later.</p>
      <button type="button" class="qv-error-retry">Try again</button>
    </div>
  `;

  const QUICKVIEW_EMPTY_HTML = `
    <div class="qv-status qv-status--empty" role="status" aria-live="polite">
      <p>No preview content available.</p>
    </div>
  `;

  function log(...args) {
    if (STATE.options.debug) console.log('[QuickView]', ...args);
  }
  function warn(...args) {
    if (STATE.options.debug) console.warn('[QuickView]', ...args);
  }

  function toSelectorList(sel) {
    if (!sel) return [];
    const arr = Array.isArray(sel) ? sel : [sel];
    return arr.filter(Boolean).map(String);
  }

  function combinedSelector(set) {
    return Array.from(set).join(',');
  }

  function findModal() {
    const modal = document.querySelector(STATE.options.modal);
    const body = document.querySelector(STATE.options.modalBody);
    return { modal, body };
  }

  function getFocusableElements(container) {
    if (!container) return [];

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll(selectors)).filter((el) => {
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      const styles = window.getComputedStyle(el);
      return styles.display !== 'none' && styles.visibility !== 'hidden';
    });
  }

  function focusFirstElement(modal) {
    if (!modal) return;

    const preferred = modal.querySelector('[data-first-focus]');
    const target = preferred || getFocusableElements(modal)[0] || modal;
    if (typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  function restoreTriggerFocus() {
    const trigger = STATE.lastTrigger;
    STATE.lastTrigger = null;

    if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
      trigger.focus({ preventScroll: true });
    }
  }

  function cleanupModalBindings() {
    if (typeof STATE.modalCleanup === 'function') {
      try {
        STATE.modalCleanup();
      } catch (e) {}
      STATE.modalCleanup = null;
    }
  }

  function bindModalAccessibility(modal) {
    if (!modal) return () => {};

    function onKeydown(e) {
      if (!STATE.isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeModalUI();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = getFocusableElements(modal);
      if (!focusable.length) {
        e.preventDefault();
        modal.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === modal) {
          e.preventDefault();
          last.focus({ preventScroll: true });
        }
        return;
      }

      if (active === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    document.addEventListener('keydown', onKeydown, true);

    return function unbindModalAccessibility() {
      document.removeEventListener('keydown', onKeydown, true);
    };
  }

  function setTriggerLoadingState(trigger, loading) {
    if (!trigger || !trigger.isConnected) return;

    trigger.classList.toggle('is-qv-loading', loading);
    trigger.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function resetModalBodyScroll(body) {
    if (!body) return;

    body.scrollTop = 0;
    body.scrollLeft = 0;

    if (typeof body.scrollTo !== 'function') return;

    try {
      body.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (e) {
      body.scrollTo(0, 0);
    }
  }

  function renderStatus(body, html) {
    if (!body) return;
    body.innerHTML = html;
    resetModalBodyScroll(body);
  }

  function openModalUI() {
    const { modal, body } = findModal();
    if (!modal || !body) return { modal: null, body: null };

    cleanupModalBindings();
    STATE.isOpen = true;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.setAttribute('aria-busy', 'true');
    resetModalBodyScroll(body);
    body.innerHTML = QUICKVIEW_LOADER_HTML;
    resetModalBodyScroll(body);
    STATE.modalCleanup = bindModalAccessibility(modal);

    window.dispatchEvent(new CustomEvent('theme:scroll:lock', {
      detail: body
    }));

    requestAnimationFrame(() => {
      if (!STATE.isOpen) return;
      focusFirstElement(modal);
    });

    return { modal, body };
  }

  function closeModalUI() {
    if (!STATE.isOpen) return;

    const { modal, body } = findModal();
    if (!modal) return;

    // 让当前请求失效，并取消在途 fetch
    STATE.requestToken += 1;
    if (STATE.activeAbortController) {
      try {
        STATE.activeAbortController.abort();
      } catch (e) {}
      STATE.activeAbortController = null;
    }
    
    cleanupModalBindings();
    cleanupQuickview();
    STATE.isOpen = false;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.removeAttribute('aria-busy');

    window.dispatchEvent(new CustomEvent('theme:scroll:unlock'));

    if (body) {
      resetModalBodyScroll(body);
      body.innerHTML = '';
      resetModalBodyScroll(body);
    }
    STATE.isLoading = false;
    updateViewDetailsButton({ visible: false });
    setTriggerLoadingState(STATE.lastTrigger, false);
    restoreTriggerFocus();
  }

  function safeContentType(type) {
    const sanitized = String(type || '')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '')
      .replace(/(^-|-$)/g, '');
    return sanitized || 'product';
  }

  function getStrategyFromEl(el) {
    const type = el?.dataset?.qvType || 'product';
    return STRATEGY_MAP[type] || STRATEGY_MAP.product;
  }

  let qvDestroyers = [];

  function bindAccordion(modalBody) {
    if (!modalBody) return () => {};

    const openTriggers = modalBody.querySelectorAll('.accordion__title.js.accordion-is-open');
    openTriggers.forEach(trigger => {
      const contentId = trigger.getAttribute('data-accordion-trigger');
      if (contentId) {
        const content = modalBody.querySelector(`#${CSS.escape(contentId)}`);
        if (content) {
          content.style.display = 'block';
        }
      }
    });

    function onAccordionClick(e) {
      const trigger = e.target.closest('.accordion__title.js');
      if (!trigger || !modalBody.contains(trigger)) return;

      const contentId = trigger.getAttribute('data-accordion-trigger');
      if (!contentId) return;

      const content = modalBody.querySelector(`#${CSS.escape(contentId)}`);
      if (!content) return;

      const isOpen = trigger.classList.contains('accordion-is-open');

      trigger.classList.toggle('accordion-is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
      content.style.display = isOpen ? 'none' : 'block';
    }

    modalBody.addEventListener('click', onAccordionClick);

    return function destroy() {
      modalBody.removeEventListener('click', onAccordionClick);
    };
  }

  function initReadMore(modalBody) {
    if (!modalBody) return () => {};

    const description = modalBody.querySelector('.description-tab');
    const readMoreBtn = modalBody.querySelector('#readMoreBtn');

    if (!description || !readMoreBtn) {
      return () => {};
    }

    let destroyed = false;

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      readMoreBtn.removeEventListener('click', onReadMoreClick);
    }

    function onReadMoreClick() {
      description.classList.add('expanded');
      readMoreBtn.style.display = 'none';
      destroy();
    }

    readMoreBtn.addEventListener('click', onReadMoreClick);
    return destroy;
  }

  function initFlickityGallery(modalBody) {
    const mainSlider = modalBody?.querySelector?.('[data-product-slideshow]');
    const thumbSlider = modalBody?.querySelector?.('[data-product-thumbs]');
    const Flickity = window.themeVendor?.Flickity;

    if (!mainSlider || !thumbSlider || !Flickity) return () => {};

    const existing = Flickity.data(mainSlider);
    if (existing) {
      try { existing.destroy(); } catch (e) {}
    }

    const flkty = new Flickity(mainSlider, {
      cellSelector: '.product__media',
      wrapAround: true,
      watchCSS: false,
      contain: true,
      accessibility: true,
      pageDots: false,
      prevNextButtons: false,
      adaptiveHeight: true,
      imagesLoaded: true,
    });

    const thumbs = Array.from(thumbSlider.querySelectorAll('.media__thumb'));

    const thumbHandlers = [];

    thumbs.forEach((thumb, index) => {
      const onThumbClick = () => {
        flkty.select(index, false, true);
        setThumbActive(index, { scrollThumb: true });
      };
      thumb.addEventListener('click', onThumbClick);
      thumbHandlers.push([thumb, onThumbClick]);
    });

    const setThumbActive = (i, options = {}) => {
      const { scrollThumb = false } = options;
      thumbs.forEach((el, idx) => {
        const isActive = idx === i;
        el.classList.toggle('is-activated', isActive);
        el.setAttribute('aria-selected', isActive ? 'true' : 'false');
        el.tabIndex = isActive ? 0 : -1;
      });
      if (scrollThumb) {
        thumbs[i]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      }
    };

    function onSelect() {
      setThumbActive(flkty.selectedIndex);
    }
    function onReady() {
      setThumbActive(flkty.selectedIndex);
    }
    function onChange(index) {
      if (!modalBody.isConnected) return;

      modalBody.querySelectorAll('.product__media video').forEach((v) => {
        if (!v.paused) v.pause();
      });

      const currentSlide = mainSlider.querySelectorAll('.product__media')[index];
      const video = currentSlide?.querySelector('video');
      if (video) {
        video.setAttribute('controls', 'true');
        video.muted = true;
        video.play().catch(() => {});
      }
    }

    flkty.on('select', onSelect);
    flkty.on('ready', onReady);
    flkty.on('change', onChange);

    let destroyed = false;

    return function destroyFlickityGallery() {
      if (destroyed) return;
      destroyed = true;

      thumbHandlers.forEach(([thumb, fn]) => {
        try { thumb.removeEventListener('click', fn); } catch (e) {}
      });

      try {
        flkty.off('select', onSelect);
        flkty.off('ready', onReady);
        flkty.off('change', onChange);
      } catch (e) {}

      try { flkty.destroy(); } catch (e) {
        console.warn('[QuickView] Flickity destroy failed', e);
      }
    };
  }

  function fixFlickityIn(modalBody) {
    const el = modalBody.querySelector('[data-product-slideshow]');
    const Flickity = window.themeVendor?.Flickity;

    if (!el || !Flickity) return;

    const flkty = Flickity.data(el);
    if (!flkty) return;

    flkty.reloadCells();
    flkty.resize();
    flkty.reposition();

    flkty.select(flkty.selectedIndex, false, true);
  }

  function scheduleFlickityFixes(modalBody, requestToken) {
    const delays = [80, 180, 360]; // ms，可按需微调

    delays.forEach((delay) => {
      setTimeout(() => {
        if (STATE.requestToken !== requestToken) return;
        fixFlickityIn(modalBody);
      }, delay);
    });
  }

  function bindSkuOnVariantChange(modalBody) {
    if (!modalBody) return () => {};

    function onVariantChange(event) {
      if (!modalBody.isConnected) return;

      const variant = event?.detail?.variant;

      const skuElements = [
        { el: modalBody.querySelector('.breadcrumb-sku-value'), hideParent: true },
        { el: modalBody.querySelector('.variant-sku'), hideParent: false },
      ].filter((item) => item.el);

      if (!skuElements.length) return;

      skuElements.forEach(({ el, hideParent }) => {
        const parent = hideParent ? el.parentElement : null;

        if (variant && variant.sku) {
          el.textContent = variant.sku;
          el.style.display = '';
          if (parent) parent.style.display = '';
        } else {
          if (parent) parent.style.display = 'none';
          else el.style.display = 'none';
        }
      });
    }

    document.addEventListener('theme:variant:change', onVariantChange);

    return function destroySkuBinding() {
      document.removeEventListener('theme:variant:change', onVariantChange);
    };
  }

  function cleanupQuickview() {
    qvDestroyers.forEach((fn) => {
      try { fn && fn(); } catch (e) {}
    });
    qvDestroyers = [];
  }

  function afterInject(modalBody) {
    cleanupQuickview();
    qvDestroyers.push(bindAccordion(modalBody));
    qvDestroyers.push(bindSkuOnVariantChange(modalBody));
    qvDestroyers.push(initReadMore(modalBody));
    qvDestroyers.push(initFlickityGallery(modalBody));
  }

  function updateViewDetailsButton({ visible, href }) {
    const btn = document.querySelector(
      '.product-quickview-custom .drawer__view-details'
    );
    if (!btn) return;

    if (visible && href) {
      btn.href = href;
      btn.style.display = '';
    } else {
      btn.href = '';
      btn.style.display = 'none';
    }
  }

  function buildUrlWithViewParam(inputUrl, strategy) {
    const url = inputUrl instanceof URL
      ? new URL(inputUrl.toString())
      : new URL(String(inputUrl), window.location.origin);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

    if (STATE.options.interceptSameOriginOnly && url.origin !== window.location.origin) {
      return null;
    }

    if (strategy?.viewParam) {
      url.searchParams.set('view', strategy.viewParam);
    }

    return url;
  }

  async function openByUrl(inputUrl, strategy) {
    const requestToken = STATE.requestToken + 1;
    STATE.requestToken = requestToken;

    if (STATE.activeAbortController) {
      try {
        STATE.activeAbortController.abort();
      } catch (e) {}
    }

    const abortController = typeof AbortController === 'function'
      ? new AbortController()
      : null;

    STATE.lastOpenRequest = { inputUrl, strategy };
    STATE.activeAbortController = abortController;
    STATE.isLoading = true;
    setTriggerLoadingState(STATE.lastTrigger, true);

    let url;
    try {
      url = buildUrlWithViewParam(inputUrl, strategy);
    } catch (err) {
      warn('Failed to build quickview URL:', inputUrl, err);
      STATE.isLoading = false;
      STATE.activeAbortController = null;
      setTriggerLoadingState(STATE.lastTrigger, false);
      return;
    }

    if (!url) {
      warn('URL is not eligible for interception:', inputUrl);
      STATE.isLoading = false;
      STATE.activeAbortController = null;
      setTriggerLoadingState(STATE.lastTrigger, false);
      return;
    }

    const { modal, body } = openModalUI();
    if (!modal || !body) {
      warn('Modal or modal body not found.');
      if (STATE.requestToken === requestToken) {
        STATE.isLoading = false;
        STATE.activeAbortController = null;
        setTriggerLoadingState(STATE.lastTrigger, false);
      }
      return;
    }

    try {
      updateViewDetailsButton({
        visible: strategy?.type === 'product',
        href: url.pathname,
      });

      log('openByUrl:', url.toString());

      const cacheKey = url.pathname + url.search;
      let html;

      if (responseCache.has(cacheKey)) {
        html = responseCache.get(cacheKey);
      } else {
        const res = await fetch(url.pathname + url.search, {
          method: 'GET',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          signal: abortController ? abortController.signal : undefined,
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);

        html = await res.text();

        if (responseCache.size >= 20) {
          responseCache.clear();
        }
        responseCache.set(cacheKey, html);
      }

      if (STATE.requestToken !== requestToken) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const selectors = Array.isArray(strategy?.contentSelectors) ? strategy.contentSelectors : [];
      const fragments = [];

      selectors.forEach((selector) => {
        doc.querySelectorAll(selector).forEach((el) => {
          fragments.push(el.outerHTML);
        });
      });

      if (!fragments.length && strategy?.fallbackToMain) {
        const main = doc.querySelector('.main-content') || doc.body;
        fragments.push(main.innerHTML);
      }

      if (!fragments.length) {
        renderStatus(body, QUICKVIEW_EMPTY_HTML);
        modal.removeAttribute('aria-busy');
        return;
      }

      if (STATE.requestToken !== requestToken) return;

      const safeType = safeContentType(strategy?.type);
      body.innerHTML = `<div class="qv-content qv-content--${safeType}">${fragments.join('')}</div>`;
      resetModalBodyScroll(body);
      modal.removeAttribute('aria-busy');

      if (strategy?.enableProductEnhancements) {
        afterInject(body);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (STATE.requestToken !== requestToken) return;
            fixFlickityIn(body);
            focusFirstElement(modal);
          });
        });

        scheduleFlickityFixes(body, requestToken);
      } else {
        requestAnimationFrame(() => {
          if (STATE.requestToken !== requestToken) return;
          focusFirstElement(modal);
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }

      if (STATE.requestToken !== requestToken) return;

      console.error('[QuickView] ERROR：', err);
      const { body: body2 } = findModal();
      if (body2) {
        renderStatus(body2, QUICKVIEW_ERROR_HTML);
        requestAnimationFrame(() => {
          if (STATE.requestToken !== requestToken) return;
          body2.querySelector('.qv-error-retry')?.focus?.({ preventScroll: true });
        });
      }
      modal.removeAttribute('aria-busy');
    } finally {
      if (STATE.requestToken === requestToken) {
        STATE.isLoading = false;
        STATE.activeAbortController = null;
        setTriggerLoadingState(STATE.lastTrigger, false);
      }
    }
  }

  async function open(handle, strategy) {
    if (!handle) return;
    return openByUrl(`/products/${encodeURIComponent(handle)}`, strategy);
  }

  function shouldIgnoreHref(rawHref) {
    if (!rawHref) return true;
    const s = String(rawHref).trim();
    if (!s) return true;
    return STATE.options.ignoreLinkSchemes.some((p) => s.startsWith(p));
  }

  function onDocumentClick(e) {
    const retryBtn = e.target.closest('.qv-error-retry');
    if (retryBtn) {
      e.preventDefault();
      e.stopPropagation();

      if (STATE.lastOpenRequest) {
        openByUrl(STATE.lastOpenRequest.inputUrl, STATE.lastOpenRequest.strategy);
      }
      return;
    }
    
    const closeSelectors = STATE.options.closeBtn;
    if (Array.isArray(closeSelectors) && closeSelectors.some(selector => e.target.closest(selector))) {
      e.preventDefault();
      e.stopPropagation();
      closeModalUI();
      return;
    }

    // 1) Link interception triggers (must be <a>)
    const linkSel = combinedSelector(STATE.linkTriggerSelectors);
    if (linkSel) {
      const linkEl = e.target.closest(linkSel);
      if (linkEl && linkEl.tagName === 'A') {
        const rawHref = linkEl.getAttribute('href') || '';
        if (!shouldIgnoreHref(rawHref)) {
          let url;

          try {
            url = new URL(rawHref, window.location.origin);
          } catch (err) {
            warn('Invalid URL for quickview interception:', rawHref, err);
            return;
          }

          if (STATE.options.interceptSameOriginOnly && url.origin !== window.location.origin) {
            return;
          }

          const strategy = getStrategyFromEl(linkEl);
          STATE.lastTrigger = linkEl;
          e.preventDefault();
          e.stopPropagation();
          openByUrl(url, strategy);
          return;
        }
      }
    }

    // 2) Handle-based triggers
    const triggerSel = combinedSelector(STATE.triggerSelectors);
    if (!triggerSel) return;

    const triggerEl = e.target.closest(triggerSel);
    if (!triggerEl) return;

    const handle = triggerEl.getAttribute('data-product-handle') || triggerEl.dataset.productHandle || null;
    if (!handle) return;

    const strategy = getStrategyFromEl(triggerEl);

    STATE.lastTrigger = triggerEl;
    e.preventDefault();
    e.stopPropagation();
    open(handle, strategy);
  }

  // ---------- Public API ----------

  function init(options = {}) {
    STATE.options = { ...STATE.options, ...options };
    STATE.options.closeBtn = toSelectorList(STATE.options.closeBtn);

    toSelectorList(STATE.options.trigger).forEach((sel) => STATE.triggerSelectors.add(sel));
    toSelectorList(STATE.options.linkTrigger).forEach((sel) => STATE.linkTriggerSelectors.add(sel));

    if (!STATE.initialized) {
      STATE.initialized = true;
      document.addEventListener('click', onDocumentClick, true);
      log('initialized triggers:', combinedSelector(STATE.triggerSelectors));
      log('initialized linkTriggers:', combinedSelector(STATE.linkTriggerSelectors));
    } else {
      log('updated triggers:', combinedSelector(STATE.triggerSelectors));
      log('updated linkTriggers:', combinedSelector(STATE.linkTriggerSelectors));
    }
  }

  window.QuickView = {
    init,
    open: (handle, type = 'product') => open(handle, STRATEGY_MAP[type] || STRATEGY_MAP.product),
    openByUrl: (url, type = 'product') => openByUrl(url, STRATEGY_MAP[type] || STRATEGY_MAP.product),
    close: closeModalUI,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
